import { z } from "zod";
import type { Allergen } from "./constants";
import type { Per100 } from "./nutrition";
import { RECIPE_MAX_LINES, type RecipeLineDto } from "./recipes";

// Paid add-on "Valori nutriționale" (owner 2026-10-28; docs/BAZA_INGREDIENTE.md). Prices are shown in euro; Stripe
// charges lei at the owner's rate 5.35, plus VAT (billing session).

export const NUTRITION_PRICES = {
  addon: { eur: 20, ronMinor: 10700 },
  renewal: { eur: 20, ronMinor: 10700 },
  pack5: { eur: 5, ronMinor: 2675, analyses: 20 },
  pack10: { eur: 10, ronMinor: 5350, analyses: 40 },
} as const;
export type NutritionPurchase = keyof typeof NUTRITION_PRICES;

/**
 * The price as the restaurant is charged: "20 € (107 lei + TVA)" in Romania (charged in lei), "20 € fără TVA
 * (taxare inversă)" when billed in euro (the same wording as Abonament și facturare).
 */
export function nutritionPrice(kind: NutritionPurchase, payCurrency: "RON" | "EUR" = "RON"): string {
  const p = NUTRITION_PRICES[kind];
  const lei = (p.ronMinor / 100).toLocaleString("ro-RO", { maximumFractionDigits: 2 });
  return payCurrency === "EUR" ? `${p.eur} € fără TVA (taxare inversă)` : `${p.eur} € (${lei} lei + TVA)`;
}

export const NUTRITION_MONTHLY_INCLUDED = 10;
export const NUTRITION_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
export const ALLERGEN_GRACE_MS = 5 * 24 * 60 * 60 * 1000;
/** Products per AI call (recipe proposal). */
export const RECIPE_BATCH = 20;

/** Mode (b): the exact text the restaurant accepts; its version is stored with the acceptance. */
export const PUBLISH_ACCEPTANCE_VERSION = "2026-11-03";
export const PUBLISH_ACCEPTANCE_TEXT =
  "Accept ca rețetele, valorile nutriționale, alergenii și aditivii (E-uri) estimați automat de Meniova pentru produsele mele să apară în meniu imediat, marcați „generate de Meniova, neconfirmate de restaurant”, fără verificarea mea prealabilă. Îmi asum răspunderea pentru informațiile afișate clienților și le voi verifica și corecta.";

export const AI_BADGE = "Generat cu AI, neverificat";

export const nutritionRunSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("confirm"), menuId: z.string().min(1).max(80) }),
  z.object({
    mode: z.literal("publish"),
    menuId: z.string().min(1).max(80),
    /** The client sends back the exact text it showed; anything else is refused. */
    acceptance: z.literal(PUBLISH_ACCEPTANCE_TEXT),
  }),
]);
export type NutritionRunInput = z.infer<typeof nutritionRunSchema>;

/** Optional subset of products (re-analysis); absent = every product of the menu without a verified recipe. */
export const nutritionAnalyzeSchema = z.object({
  productIds: z.array(z.string().min(1).max(80)).max(500).optional(),
});

export interface NutritionStatusDto {
  active: boolean;
  /** Purchase of the current year; null when never paid or expired. */
  endsAt: number | null;
  everPaid: boolean;
  monthlyIncluded: number;
  monthlyUsed: number;
  /** Analyses left in valid packs. */
  credits: number;
  packs: { analyses: number; used: number; expiresAt: number }[];
  /** The initial menu has not been analysed yet with the current purchase. */
  initialAvailable: boolean;
  prices: typeof NUTRITION_PRICES;
  /** A test account the owner marked (migration 0044): the add-on is activated without payment. */
  complimentary: boolean;
  /** The restaurant accepted that Meniova's estimates are published automatically (owner 2026-11-03). */
  accepted: boolean;
}

export interface NutritionProductDto {
  id: string;
  name: string;
  categoryName: string;
  portion: string;
  recipeSource: "manual" | "ai" | null;
  verifiedAt: number | null;
  /** AI recipe not verified by the restaurant: "Generat cu AI, neverificat". */
  aiUnverified: boolean;
  lines: number;
  missing: string[];
  kcalPer100: number | null;
  published: "ai" | "verified" | null;
  allergensConfirmed: boolean;
}

export interface NutritionRunDto {
  id: string;
  menuId: string;
  mode: "confirm" | "publish";
  status: "running" | "done" | "stopped";
  startedAt: number;
  finishedAt: number | null;
  productsTotal: number;
  productsDone: number;
  productsMissing: number;
  stopReason: string | null;
}

/** Published values of a product (public menu, export). */
export interface PublishedNutrition {
  grams: number;
  per100g: Record<string, number | null>;
  perPortion: Record<string, number | null>;
  allergens: string[];
  traces: string[];
  additives: string[];
  frozen: boolean;
  incomplete: string[];
  ingredients: { name: string; grams: number }[];
  /** Set when read for the public menu: Meniova's estimate the restaurant has not verified. */
  unverified?: boolean;
}

export interface AllergenDeadlineDto {
  productId: string;
  name: string;
  menuId: string;
  deadlineAt: number;
  expired: boolean;
}

/**
 * A base preparation (blat de pizza, sos demiglace…): its recipe per its own total weight. "standard" = proposed by
 * the AI from the food database, "own" = entered or changed by the restaurant (owner 2026-11-02).
 */
export interface BaseRecipeDto {
  id: string;
  name: string;
  source: "standard" | "own";
  status: "proposed" | "confirmed";
  lines: RecipeLineDto[];
  /** Total weight of the lines; the values are per 100 g of it. */
  grams: number;
  per100: Per100;
  allergens: Allergen[];
}

export const baseRecipeSaveSchema = z.object({
  name: z.string().trim().min(1).max(80),
  lines: z
    .array(
      z.object({
        kind: z.enum(["food", "product"]),
        id: z.string().trim().min(1).max(80),
        grams: z.number().positive().max(5000),
      }),
    )
    .min(1)
    .max(RECIPE_MAX_LINES),
  /** The restaurant confirmed the standard recipe as it was proposed. */
  standard: z.boolean().optional(),
});
export type BaseRecipeSave = z.infer<typeof baseRecipeSaveSchema>;
